<script>
  import get from "lodash/get";
  import sortBy from "lodash/sortBy";
  import { search as fuzzy } from "fast-fuzzy";
  import cleanUpApiString from "../../../../helpers/cleanUpApiString";
  import MinusIcon from "../../../Common/Icons/MinusIcon.svelte";
  import PlusIcon from "../../../Common/Icons/PlusIcon.svelte";
  import SearchIcon from "../../../Common/Icons/SearchIcon.svelte";
  import { appState, queryState } from "../../../../stores";

  // Props
  export let key;
  export let heading;
  export let inModal = false;

  // Constants
  const queryKey = key === "sectors" ? "sectorIds" : key;
  const placeholder = `Search ${heading
    .toLowerCase()
    .replace("report ", "")
    .replace("fiscal ", "")
    .replace("published ", "")}${
    heading.charAt(heading.length - 1) === "s" ? "" : "s"
  }...`;
  const isYearKey = (key) => ["fiscalYears", "publishedYears"].includes(key);

  // State
  let search = "";
  $: disabled = $appState.loading;
  $: expanded = inModal ? false : nodes?.length && nodes.length > 0;
  $: checked = $queryState[queryKey] ?? [];
  $: nodes = get($appState.updatedFilters, key, [])
    .filter((node) => node.id)
    .map((node) => {
      if (isYearKey(key)) node.name = node.id.toString();
      if (key === "sectors")
        node.name =
          WP.sectorIdMap.find((wpSec) => node.id === wpSec.id)?.title ||
          node.name;
      if (key === "reportTypes") node.name = cleanUpApiString(node.name);
      return node;
    });
  $: sortedNodes = isYearKey(key)
    ? sortBy(nodes, "id").reverse()
    : sortBy(nodes, "name");
  $: visibleNodes =
    search !== ""
      ? sortBy(
          fuzzy(search, sortedNodes, {
            keySelector: (node) => node.name,
            threshold: isYearKey(key) ? 1 : 0.6,
          }),
          "name"
        )
      : sortedNodes;

  // Handlers
  function handleChange(e) {
    let { value, checked: isChecked } = e.target;
    if (key !== "organizations") value = parseInt(value);

    const newArr = isChecked
      ? [...checked, value]
      : checked.filter((id) => id !== value);

    queryState.update((queryState) => ({
      ...queryState,
      [queryKey]: newArr.length ? newArr : null,
      pg: null,
    }));

    if (inModal) {
      requestAnimationFrame(() => {
        const target = document.getElementById("mobile-results-and-filter");
        target.scrollIntoView(true);
      });
    }
  }
  function toggleExpanded() {
    expanded = !expanded;
  }
</script>

{#if expanded}
  <div
    class={`filter-section expanded ${inModal ? "modal-version" : ""}`}
    {disabled}
  >
    <div
      class="filter-section-title"
      on:click={inModal ? toggleExpanded : null}
    >
      <button
        class="expand-btn"
        on:click|preventDefault={inModal ? null : toggleExpanded}
        {disabled}
      >
        <MinusIcon />
      </button>
      <h3>{heading}</h3>
    </div>
    {#if nodes.length}
      <div class="search-input-wrapper">
        <div class="search-input-icon">
          <SearchIcon />
        </div>
        <input type="search" bind:value={search} {placeholder} />
      </div>
      <div class="checkbox-wrap">
        {#each visibleNodes as node}
          <div class="filter-section-checkbox-set">
            <label for={`${key}_${node.id}`}>
              <input
                type="checkbox"
                id={`${key}_${node.id}`}
                value={node.id}
                checked={checked.includes(node.id)}
                on:change={handleChange}
                {disabled}
              />
              <span>{node.name || node.id}</span>
            </label>
            <span class="number-of-results">{node.numberOfResults}</span>
          </div>
        {/each}
      </div>
    {:else}
      <p class="empty-message">
        No {heading.toLowerCase()} match your search. Try expanding your filters,
        or use another search term.
      </p>
    {/if}
  </div>
{:else}
  <div class={`filter-section collapsed ${inModal ? "modal-version" : ""}`}>
    <div
      class="filter-section-title"
      on:click={inModal ? toggleExpanded : null}
    >
      <button
        class="expand-btn"
        on:click|preventDefault={inModal ? null : toggleExpanded}
        {disabled}
      >
        <PlusIcon />
      </button>
      <h3>{heading}</h3>
    </div>
  </div>
{/if}

<style lang="scss">
  .filter-section {
    background: #f9f9f9;
    border-radius: 10px;
    padding: 20px 20px 20px 17px;
    margin-bottom: 23px;
  }

  .modal-version {
    width: 100%;
    margin-bottom: 0px;
    min-height: auto;
    height: auto;
    box-shadow: none;
    padding: 0;
  }

  .modal-version.expanded {
    position: fixed;
    top: 75px;
    height: calc(100vh - 88px - 58px - 30px);
    border-radius: 0;
    border-bottom: 2px solid #ebe9e9;

    display: flex;
    flex-direction: column;
  }

  .filter-section[disabled="true"] * {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .filter-section-title {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .modal-version .filter-section-title {
    cursor: pointer;
    padding: 20px;
    margin-bottom: 0;
  }

  .filter-section-title h3 {
    font-family: "Segoe UI", sans-serif;
    font-style: normal;
    font-weight: 500; /*design calls for 600, but this looks more like design, because of Avenir vs Segoe UI font?*/
    font-size: 24px;
    line-height: 30px;
    color: #4a4a4a;
  }

  .checkbox-wrap {
    max-height: 240px;
    overflow-y: scroll;
    padding: 0 20px 0 16px;
  }

  .modal-version .checkbox-wrap {
    max-height: none;
    padding: 0 24px;
  }

  .filter-section-checkbox-set {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
  }

  .number-of-results,
  label {
    font-family: "Segoe UI", sans-serif;
    font-style: normal;
    font-weight: 400; //designs call for 500, but this looks more similar because of Segoe UI vs Avenir font family
    font-size: 14px;
    line-height: 22px;
    color: #9b9b9b;
  }

  label {
    padding-right: 16px;
    width: 100%;
    cursor: pointer;

    span {
      padding-left: 26px;
      position: relative;
      display: inline-block;
      color: #4a4a4a;
    }

    &:hover,
    &:focus {
      opacity: 0.7;
    }
  }

  label > input[type="checkbox"] {
    display: none;
  }

  label > input[type="checkbox"] + *::before {
    content: "";
    display: inline-block;
    position: absolute;
    left: 0;
    top: 3px;
    width: 15px;
    height: 15px;
    border-radius: 2px;
    border-style: solid;
    border-width: 1px;
    border-color: #d07743;
    margin-right: 11.44px;
  }

  label > input[type="checkbox"]:checked + *::before {
    content: "\2713";
    color: white;
    text-align: center;
    vertical-align: middle;
    height: 15px;
    line-height: 15px;
    background: #d07743;
    border-color: #d07743;
  }

  .expand-btn {
    border: none;
    background: transparent;
    cursor: pointer;
    color: #d07743;
    height: 50px;
    width: 50px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 0;

    &:hover,
    &:focus {
      opacity: 0.7;
    }
  }

  .empty-message {
    font-size: 14px;
    line-height: 22px;
  }

  /* width */
  ::-webkit-scrollbar {
    width: 6px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    box-shadow: transparent;
    border-radius: 5px;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: #c4c4c4;
    border-radius: 5px;
  }
  .search-input-wrapper {
    margin: 5px 0 25px;
    padding-left: 16px;

    position: relative;
    display: flex;
    align-items: center;

    input {
      font-family: "Segoe UI", sans-serif;
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 22px;
      padding: 9px 10px 7px 40px;
      background: #ffffff;
      color: #4a4a4a;
      width: 100%;

      border: 1px solid #e7e5e2;
      border-radius: 10px;
      outline: none;
    }

    :global(::placeholder) {
      color: #9b9b9b;
    }
  }
  .modal-version .search-input-wrapper {
    padding-left: 15px;
    padding-right: 15px;
    margin-top: 0px;
    margin-bottom: 48px;
  }

  .modal-version .search-input-wrapper input {
    background: #ebe9e9;
    border: none;
    padding-left: 50px;
    height: 45px;
  }
  .search-input-icon {
    color: #a8a4a1;
    width: 16px;
    height: 16px;
    position: absolute;
    top: 10px;
    margin-left: 16px;
    display: flex;
  }
</style>
